import React from "react";
import {
  AlertTriangle,
  Clock,
  Hourglass,
  Lock,
  Moon,
  Play,
  Square,
  Sun,
  Sunrise,
  Sunset,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/shared/utils/cn";
import { Button } from "@/components/ui/button";
import type { ScheduledShift, ScheduledShiftStatus } from "../types";
import { formatTimeInZone } from "../utils/formatShiftTime";
import {
  formatDurationHm,
  isSameDayInZone,
  scheduledDurationMs,
} from "../utils/scheduledShiftRange";

interface ScheduledShiftCardProps {
  shift: ScheduledShift;
  /**
   * Called when the employee taps "Start Shift". The page wires this into the
   * existing confirm-start flow. When omitted the Start Shift button is hidden.
   */
  onStart?: (shift: ScheduledShift) => void;
  /** Disables the Start Shift button while a location fetch is in flight. */
  isStartDisabled?: boolean;
  /**
   * True when this scheduled shift's corresponding actual shift is currently
   * open (clocked in but not yet ended). Replaces "Start Shift" with
   * "End Shift" and shows an "In Progress" badge.
   */
  isOpen?: boolean;
  /** Called when the employee taps "End Shift" on an open card. */
  onEnd?: (shift: ScheduledShift) => void;
  className?: string;
}

interface StatusPresentation {
  label: string;
  className: string;
}

function getStatusPresentation(status: ScheduledShiftStatus, isOpen?: boolean): StatusPresentation {
  if (isOpen) {
    return { label: "In Progress", className: "bg-green-50 text-green-700 border-green-300" };
  }
  switch (status) {
    case "confirmed":
      return { label: "Confirmed", className: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    case "cancelled":
      return { label: "Cancelled", className: "bg-red-50 text-red-700 border-red-200" };
    case "completed":
      return { label: "Completed", className: "bg-blue-50 text-blue-700 border-blue-200" };
    case "missed":
      return { label: "Missed", className: "bg-orange-50 text-orange-700 border-orange-200" };
    case "no_show":
      return { label: "No Show", className: "bg-red-50 text-red-700 border-red-200" };
    case "scheduled":
      return { label: "Scheduled", className: "bg-gray-50 text-gray-700 border-gray-200" };
    case "unscheduled":
      return { label: "Unscheduled", className: "bg-purple-50 text-purple-700 border-purple-200" };
    default:
      return {
        label: status ? status.charAt(0).toUpperCase() + status.slice(1) : "Scheduled",
        className: "bg-gray-50 text-gray-700 border-gray-200",
      };
  }
}

type TimeOfDay = "morning" | "afternoon" | "evening" | "night";

interface TimeOfDayPresentation {
  title: string;
  icon: LucideIcon;
  /** Tailwind classes for the icon container background. */
  iconWrapperClass: string;
  /** Tailwind classes for the icon stroke color. */
  iconClass: string;
}

const TIME_OF_DAY_PRESENTATION: Record<TimeOfDay, TimeOfDayPresentation> = {
  morning: {
    title: "Morning Shift",
    icon: Sunrise,
    iconWrapperClass: "bg-amber-50",
    iconClass: "text-amber-500",
  },
  afternoon: {
    title: "Afternoon Shift",
    icon: Sun,
    iconWrapperClass: "bg-orange-50",
    iconClass: "text-orange-500",
  },
  evening: {
    title: "Evening Shift",
    icon: Sunset,
    iconWrapperClass: "bg-violet-50",
    iconClass: "text-violet-500",
  },
  night: {
    title: "Night Shift",
    icon: Moon,
    iconWrapperClass: "bg-indigo-50",
    iconClass: "text-indigo-500",
  },
};

/**
 * Pick a time-of-day bucket from a shift's planned start hour in its
 * timezone. Buckets align with the copy used for the title and the icon:
 *   - Night:     21:00–04:59  (late-evening / overnight starts)
 *   - Morning:   05:00–11:59
 *   - Afternoon: 12:00–16:59
 *   - Evening:   17:00–20:59
 */
function getTimeOfDay(shift: ScheduledShift): TimeOfDay {
  const start = new Date(shift.planned_start_at);
  if (Number.isNaN(start.getTime())) return "morning";
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      hour12: false,
      timeZone: shift.timezone || undefined,
    }).format(start)
  );
  if (!Number.isFinite(hour)) return "morning";
  if (hour >= 21 || hour < 5) return "night";
  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
}

function deriveSubtitle(shift: ScheduledShift): string | null {
  const loc = shift.location;
  if (!loc) return null;
  if (loc.department && loc.name) return `${loc.department} - ${loc.name}`;
  return loc.name || loc.department || null;
}

function hasScheduleDifference(shift: ScheduledShift): boolean {
  const comparison = shift.scheduled_comparison;
  if (!comparison) return false;
  const hasStartDelta =
    typeof comparison.start_diff_minutes === "number" && comparison.start_diff_minutes !== 0;
  const hasEndDelta =
    typeof comparison.end_diff_minutes === "number" && comparison.end_diff_minutes !== 0;
  return (
    comparison.early_start ||
    comparison.late_start ||
    comparison.early_end ||
    comparison.late_end ||
    hasStartDelta ||
    hasEndDelta
  );
}

export function ScheduledShiftCard({
  shift,
  onStart,
  isStartDisabled,
  isOpen = false,
  onEnd,
  className,
}: ScheduledShiftCardProps) {
  const status = getStatusPresentation(shift.status, isOpen);
  const timeOfDay = getTimeOfDay(shift);
  const presentation = TIME_OF_DAY_PRESENTATION[timeOfDay];
  const IconComponent = presentation.icon;
  const subtitle = deriveSubtitle(shift);
  const startLabel = formatTimeInZone(shift.planned_start_at, shift.timezone);
  const endLabel = shift.planned_end_at
    ? formatTimeInZone(shift.planned_end_at, shift.timezone)
    : isOpen
    ? "In progress"
    : "—";
  const duration =
    isOpen && !shift.planned_end_at
      ? "In progress"
      : formatDurationHm(scheduledDurationMs(shift));
  const differsFromSchedule = hasScheduleDifference(shift);

  // Show the End Shift button when this shift is currently open.
  const hasEndButton = isOpen && onEnd != null;

  // Show the Start button whenever the shift could conceivably be started; it's
  // only actually enabled when the planned start is today in the shift's
  // timezone. Completed/cancelled/missed/unscheduled shifts hide the button.
  const hasStartButton =
    !isOpen &&
    onStart != null &&
    (shift.status === "scheduled" || shift.status === "confirmed") &&
    !shift.actual_shift_id;
  const isToday = isSameDayInZone(shift.planned_start_at, shift.timezone);
  const canStartNow = hasStartButton && isToday && !isStartDisabled;

  return (
    <article
      className={cn(
        "bg-white rounded-2xl shadow-sm border p-4 space-y-4 w-full max-w-full min-w-0 overflow-hidden",
        isOpen ? "border-green-300 shadow-green-100" : "border-gray-100",
        className
      )}
      data-testid={`scheduled-shift-card-${shift.id}`}
    >
      {isOpen && (
        <div className="flex items-center gap-1.5 -mb-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">
            Shift in progress
          </span>
        </div>
      )}

      <header className="flex items-start justify-between gap-3 min-w-0">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className={cn(
              "flex-shrink-0 rounded-xl p-2.5",
              presentation.iconWrapperClass
            )}
          >
            <IconComponent
              className={cn("h-5 w-5", presentation.iconClass)}
              aria-hidden="true"
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 truncate m-0">
              {presentation.title}
            </h3>
            {subtitle ? (
              <p className="text-sm text-gray-500 truncate">{subtitle}</p>
            ) : null}
          </div>
        </div>
        <span
          className={cn(
            "flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full border",
            status.className
          )}
          data-testid={`scheduled-shift-status-${shift.id}`}
        >
          {status.label}
        </span>
      </header>

      <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-xl p-3 min-w-0">
        <div className="flex items-start gap-2 min-w-0">
          <Clock className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Start</p>
            <p className="text-sm font-semibold text-gray-900 truncate">{startLabel}</p>
          </div>
        </div>
        <div className="flex items-start gap-2 min-w-0">
          <Clock className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">End</p>
            <p className="text-sm font-semibold text-gray-900 truncate">{endLabel}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
        <Hourglass className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
        <span className="truncate">
          Duration: <span className="font-medium text-gray-800">{duration}</span>
        </span>
      </div>

      {differsFromSchedule ? (
        <div
          className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800"
          data-testid={`scheduled-shift-diff-${shift.id}`}
        >
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <span>Times differed</span>
        </div>
      ) : null}

      {hasEndButton ? (
        <Button
          type="button"
          onClick={() => onEnd?.(shift)}
          className="w-full max-w-full font-semibold py-3 rounded-xl shadow-sm flex items-center justify-center gap-2 transition-colors bg-red-600 hover:bg-red-700 active:bg-red-800 text-white"
          data-testid={`scheduled-shift-end-${shift.id}`}
        >
          <Square className="h-4 w-4" aria-hidden="true" />
          End Shift
        </Button>
      ) : null}

      {hasStartButton ? (
        <Button
          type="button"
          onClick={() => canStartNow && onStart?.(shift)}
          disabled={!canStartNow}
          aria-disabled={!canStartNow}
          title={
            canStartNow
              ? undefined
              : isStartDisabled
              ? "Getting location…"
              : "Available on the day of the shift"
          }
          className={cn(
            "w-full max-w-full font-semibold py-3 rounded-xl shadow-sm flex items-center justify-center gap-2 transition-colors",
            canStartNow
              ? "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white"
              : "bg-gray-100 text-gray-400 cursor-not-allowed opacity-100 disabled:opacity-100 hover:bg-gray-100"
          )}
          data-testid={`scheduled-shift-start-${shift.id}`}
        >
          {canStartNow ? (
            <Play className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Lock className="h-4 w-4" aria-hidden="true" />
          )}
          {canStartNow
            ? "Start Shift"
            : isStartDisabled
            ? "Getting location…"
            : "Start Shift"}
        </Button>
      ) : null}
    </article>
  );
}
